<template>
    <div id="groups">
      <v-container fluid>
        <!-- Discover Groups Section -->
        <v-card class="mx-auto mb-4" outlined>
          <v-card-title class="text-h5">
            <v-icon left>mdi-map-search</v-icon>
            {{ t("Discover Groups") }}
            <v-spacer></v-spacer>
            <v-select
              :items="languages"
              v-model="selectedLanguage"
              @change="setLanguage(selectedLanguage)"
              style="max-width: 80px"
              dense
              hide-details
              :menu-props="{ zIndex: 1000 }"
            ></v-select>
          </v-card-title>
          
          <!-- Groups Map -->
          <div v-if="allGroups.length > 0" class="groups-map-container">
            <l-map
              :zoom="zoom"
              :center="center"
              :options="mapOptions"
              style="height: 400px; width: 100%"
              @click="selectedMapGroup = null"
            >
              <l-tile-layer :url="urlStates" :attribution="attribution" />
              <l-marker
                v-for="group in filteredGroups"
                :key="group.id"
                :lat-lng="group.location"
                @click="selectGroupOnMap(group)"
              >
                <l-tooltip>{{ group.groupName }}</l-tooltip>
              </l-marker>
            </l-map>
          </div>
          
          <!-- Search and Filter Bar -->
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="searchQuery"
                  prepend-icon="mdi-magnify"
                  :label="t('Search groups by name or description')"
                  clearable
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filterStatus"
                  :items="statusFilters"
                  :label="t('Filter by status')"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="sortBy"
                  :items="sortOptions"
                  :label="t('Sort by')"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
          
          <!-- Groups List -->
          <v-list three-line>
            <v-list-item
              v-for="group in paginatedGroups"
              :key="group.id"
              :class="{ 'blue lighten-5': selectedMapGroup && selectedMapGroup.id === group.id }"
            >
              <v-list-item-avatar v-if="group.mainLogo && !failedImages.has(group.id)" size="60">
                <v-img 
                  :src="getLogoUrl(group.mainLogo)"
                  @error="() => handleImageError(group.id)"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-list-item-avatar>
              <v-list-item-avatar v-else size="60" color="primary">
                <v-icon dark>mdi-image-off</v-icon>
              </v-list-item-avatar>
              
              <v-list-item-content>
                <v-list-item-title class="text-h6">
                  {{ group.groupName }}
                  <v-chip
                    x-small
                    :color="getStatusColor(group)"
                    text-color="white"
                    class="ml-2"
                  >
                    {{ getStatusText(group) }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ group.textDescription || t('No description available') }}
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  <v-icon small>mdi-map-marker</v-icon>
                  {{ group.haikuboxes.length }} {{ t("stations") }}
                  <span v-if="group.memberDeviceCount">
                    • <v-icon small>mdi-check</v-icon>
                    {{ group.memberDeviceCount }} {{ t("of your devices") }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              
              <v-list-item-action>
                <v-btn
                  color="primary"
                  text
                  @click="viewGroup(group)"
                >
                  {{ t("View") }}
                </v-btn>
                <v-btn
                  v-if="canRequestToJoin(group)"
                  color="success"
                  text
                  @click="showJoinDialog(group)"
                >
                  {{ t("Add Devices") }}
                </v-btn>
                <v-btn
                  v-if="group.pendingDevices && group.pendingDevices.length > 0"
                  color="orange"
                  text
                  disabled
                >
                  {{ group.pendingDevices.length }} {{ t("Pending") }}
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          
          <!-- Pagination -->
          <v-card-actions v-if="filteredGroups.length > groupsPerPage">
            <v-spacer></v-spacer>
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              circle
            ></v-pagination>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
  
        <!-- Your Groups Section -->
        <v-card v-if="user" class="mx-auto mb-4" outlined>
          <v-tabs v-model="managementTab">
            <v-tab>
              <v-icon left>mdi-connection</v-icon>
              {{ t("Your Group Memberships") }}
            </v-tab>
            <v-tab>
              <v-icon left>mdi-account-key</v-icon>
              {{ t("Groups You Manage") }}
            </v-tab>
          </v-tabs>
          
          <v-tabs-items v-model="managementTab">
            <!-- Tab 1: Your Memberships -->
            <v-tab-item>
              <v-data-table
                v-if="membershipData.length > 0"
                :headers="membershipHeaders"
                :items="membershipData"
                :items-per-page="10"
                class="elevation-0"
              >
                <template v-slot:item.devices="{ item }">
                  <v-chip
                    v-for="device in item.devices"
                    :key="device.serial"
                    small
                    class="ma-1"
                  >
                    {{ device.name }}
                  </v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="viewGroup(item.group)"
                  >
                    {{ t("View Dashboard") }}
                  </v-btn>
                  <v-btn
                    v-if="item.canAddMore && item.group.owner !== user"
                    small
                    text
                    color="success"
                    @click="showJoinDialog(item.group)"
                  >
                    {{ t("Add More") }}
                  </v-btn>
                  <v-btn
                    v-if="item.group.owner !== user"
                    small
                    text
                    color="error"
                    @click="leaveGroup(item)"
                  >
                    {{ t("Leave") }}
                  </v-btn>
                  <v-chip
                    v-if="item.group.owner === user"
                    small
                    color="blue"
                    text-color="white"
                    class="ml-2"
                  >
                    {{ t("Owner") }}
                  </v-chip>
                </template>
              </v-data-table>
              <v-card-text v-else class="text-center py-8">
                <v-icon size="48" color="grey">mdi-information-outline</v-icon>
                <p class="mt-4">{{ t("You haven't joined any groups yet.") }}</p>
                <p>{{ t("Discover groups above and add your Haikuboxes to start collaborating!") }}</p>
              </v-card-text>
            </v-tab-item>
            
            <!-- Tab 2: Groups You Manage -->
            <v-tab-item>
              <v-card-text>
                <v-btn color="primary" @click="createNewGroup" class="mb-4">
                  <v-icon left>mdi-plus</v-icon>
                  {{ t("Create New Group") }}
                </v-btn>
              </v-card-text>
              
              <v-list v-if="myGroups.length > 0">
                <v-list-item
                  v-for="group in myGroups"
                  :key="group.id"
                >
                  <v-list-item-avatar v-if="group.mainLogo && !failedImages.has(group.id)">
                    <v-img 
                      :src="getLogoUrl(group.mainLogo)"
                      @error="() => handleImageError(group.id)"
                    >
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                  </v-list-item-avatar>
                  <v-list-item-avatar v-else color="primary">
                    <v-icon dark>mdi-image-off</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ group.groupName }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ group.haikuboxes.length }} {{ t("Haikuboxes") }}
                      <span v-if="getRequestCount(group.id) > 0">
                        • <v-icon small color="orange">mdi-alert</v-icon>
                        {{ getRequestCount(group.id) }} {{ t("pending requests") }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon @click="editGroup(group)">
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon @click.stop="deleteGroup(group)">
                      <v-icon color="red">mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-card-text v-else class="text-center py-8">
                <v-icon size="48" color="grey">mdi-folder-open-outline</v-icon>
                <p class="mt-4">{{ t("You haven't created any groups yet.") }}</p>
              </v-card-text>
              
              <!-- Join Requests for Managed Groups -->
              <v-card v-if="joinRequests.length > 0" outlined class="ma-4">
                <v-card-title>{{ t("Pending Join Requests") }}</v-card-title>
                <v-list>
                  <v-list-item
                    v-for="request in joinRequests"
                    :key="request.id"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ request.haikuboxName }} ({{ request.haikuboxSerial }})
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        For group: {{ request.groupName }}
                        <br>
                        Requested by: {{ request.requestorId }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn-toggle>
                        <v-btn small color="success" @click="approveRequest(request)">
                          Approve
                        </v-btn>
                        <v-btn small color="error" @click="rejectRequest(request)">
                          Reject
                        </v-btn>
                      </v-btn-toggle>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
  
        <!-- Join Group Dialog -->
        <v-dialog v-model="showJoinGroupDialog" max-width="600px">
          <v-card>
            <v-card-title>
              {{ t("Add Devices to") }} {{ selectedGroup ? selectedGroup.groupName : '' }}
            </v-card-title>
            <v-card-text>
              <v-form ref="joinForm">
                <p class="mb-4">{{ t("Select which of your Haikuboxes you'd like to add to this group:") }}</p>
                
                <v-checkbox
                  v-for="device in availableDevicesForGroup"
                  :key="device.serial"
                  v-model="devicesToAdd"
                  :value="device.serial"
                  :disabled="device.disabled"
                >
                  <template v-slot:label>
                    <span>
                      {{ device.name }} ({{ device.serial }})
                      <v-chip v-if="device.status === 'member'" x-small color="success" class="ml-2">
                        {{ t("Already member") }}
                      </v-chip>
                      <v-chip v-if="device.status === 'pending'" x-small color="orange" class="ml-2">
                        {{ t("Request pending") }}
                      </v-chip>
                    </span>
                  </template>
                </v-checkbox>
                
                <v-textarea
                  v-model="joinMessage"
                  :label="t('Message to group owner (optional)')"
                  :hint="t('Explain why you\'d like to join this group')"
                  rows="3"
                  counter="500"
                  maxlength="500"
                  outlined
                  class="mt-4"
                ></v-textarea>
                
                <v-alert v-if="devicesToAdd.length === 0" type="info" dense class="mt-4">
                  {{ t("Please select at least one device to add to the group.") }}
                </v-alert>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="cancelJoin">{{ t("Cancel") }}</v-btn>
              <v-btn
                color="primary"
                :disabled="devicesToAdd.length === 0"
                :loading="joinLoading"
                @click="submitJoinRequest"
              >
                {{ t("Send Request") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
  
        <!-- Create/Edit Group Dialog -->
        <v-dialog v-model="showCreateDialog" max-width="800px" persistent @input="onDialogChange">
          <v-card>
            <v-card-title>
              {{ editingGroup ? t('Edit Group') : t('Create New Group') }}
            </v-card-title>
            <v-card-text>
              <v-form ref="groupForm" v-model="formValid" v-if="showCreateDialog">
                <v-text-field
                  v-model="groupForm.groupName"
                  :label="t('Group Name')"
                  :rules="[v => !!v || 'Group name is required']"
                  required
                ></v-text-field>
                
                <v-text-field
                  v-model="groupForm.groupSlug"
                  :label="t('URL Slug')"
                  :rules="slugRules"
                  hint="This will be used in the URL: listen.haikubox.com/#/groups/[slug]"
                  persistent-hint
                  required
                  @input="onSlugInput"
                  :loading="checkingSlug"
                  :success="slugAvailable && groupForm.groupSlug && !slugError"
                  :error="!!slugError"
                  :error-messages="slugError"
                  :success-messages="slugAvailable && groupForm.groupSlug && !slugError ? 'This slug is available!' : ''"
                ></v-text-field>
                
                <v-textarea
                  v-model="groupForm.textDescription"
                  :label="t('Description')"
                  rows="3"
                ></v-textarea>
                
                <v-text-field
                  v-model="groupForm.linkToOrg"
                  :label="t('Organization Website')"
                  placeholder="https://example.com"
                  :rules="urlRules"
                ></v-text-field>

                <!-- Data policy (enforced by the groups API) -->
                <v-card class="my-4" outlined>
                  <v-card-title>{{ t("Data policy") }}</v-card-title>
                  <v-card-text>
                    <v-autocomplete
                      v-model="groupForm.hiddenSpecies"
                      :items="speciesOptions"
                      item-text="text"
                      item-value="value"
                      :filter="filterSpecies"
                      :label="t('Hidden species')"
                      hint="Detections of these species are removed from the group page, its statistics and its audio."
                      persistent-hint
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      clearable
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.text }}</v-list-item-title>
                          <v-list-item-subtitle class="font-italic">{{ item.sn }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                    <v-select
                      v-model="groupForm.minLevel"
                      :items="minLevelOptions"
                      :label="t('Minimum confidence')"
                      hint="Lower-confidence detections are removed for everyone viewing the group."
                      persistent-hint
                      class="mt-4"
                    ></v-select>
                  </v-card-text>
                </v-card>

                <!-- Viewer features (hidden on the group page, not enforced by the API) -->
                <v-card class="my-4" outlined>
                  <v-card-title>{{ t("Viewer features") }}</v-card-title>
                  <v-card-text>
                    <v-switch
                      v-model="groupForm.allowDatePicker"
                      :label="t('Date picker (view earlier days, up to 30 days back)')"
                      dense
                      hide-details
                    ></v-switch>
                    <v-switch
                      v-model="groupForm.allowCsvDownload"
                      :label="t('CSV download')"
                      dense
                      hide-details
                    ></v-switch>
                    <v-switch
                      v-model="groupForm.allowAudioDownload"
                      :label="t('Audio download')"
                      dense
                      hide-details
                    ></v-switch>
                  </v-card-text>
                </v-card>

                <!-- Access key -->
                <v-card class="my-4" outlined>
                  <v-card-title>{{ t("Access key") }}</v-card-title>
                  <v-card-text>
                    <div v-if="!editingGroup">
                      {{ t("Save the group first, then edit it to add an access key.") }}
                    </div>
                    <v-progress-linear v-else-if="accessKeyLoading" indeterminate></v-progress-linear>
                    <div v-else-if="!accessKey">
                      <p>{{ t("Open: anyone with the group's link can see its detections.") }}</p>
                      <v-btn color="primary" outlined @click="newAccessKey">
                        <v-icon left>mdi-lock</v-icon>{{ t("Require an access key") }}
                      </v-btn>
                    </div>
                    <div v-else>
                      <p>
                        {{ t("Private: viewers need this key to see detections and audio. The group's name, description, logos and map stay public.") }}
                      </p>
                      <v-text-field
                        :value="accessKeyLink()"
                        :label="t('Share link (opens the group already unlocked)')"
                        readonly
                        append-icon="mdi-content-copy"
                        @click:append="copyText(accessKeyLink())"
                      ></v-text-field>
                      <v-text-field
                        :value="accessKey"
                        :label="t('Key')"
                        readonly
                        append-icon="mdi-content-copy"
                        @click:append="copyText(accessKey)"
                      ></v-text-field>
                      <v-btn text color="primary" @click="newAccessKey">
                        <v-icon left>mdi-refresh</v-icon>{{ t("New key") }}
                      </v-btn>
                      <v-btn text color="error" @click="removeAccessKey">
                        <v-icon left>mdi-lock-open</v-icon>{{ t("Remove key") }}
                      </v-btn>
                    </div>
                    <v-alert v-if="accessKeyError" type="error" dense text class="mt-2">
                      {{ accessKeyError }}
                    </v-alert>
                  </v-card-text>
                </v-card>

                <!-- Haikubox Selection -->
                <v-card class="my-4" outlined>
                  <v-card-title>{{ t("Select Haikuboxes") }}</v-card-title>
                  <v-card-text>
                    <v-alert v-if="userDevices.length === 0" type="info" dense>
                      {{ t("No Haikuboxes found. Please ensure you have claimed at least one Haikubox.") }}
                    </v-alert>
                    <v-checkbox
                      v-for="device in userDevices"
                      :key="device.serial"
                      v-model="selectedHaikuboxes"
                      :value="device.serial"
                      :label="`${device.name} (${device.serial})`"
                    ></v-checkbox>
                  </v-card-text>
                </v-card>
  
                <!-- Map Marker Selection -->
                <v-select
                  v-model="groupForm.mapMarker"
                  :items="selectedDevicesWithNames"
                  item-text="display"
                  item-value="serial"
                  :label="t('Select location for map marker')"
                  :rules="[v => !!v || 'Please select a location']"
                ></v-select>
  
                <!-- Logo Upload -->
                <v-file-input
                  v-model="logoFile"
                  :label="t('Group Logo')"
                  accept="image/*"
                  prepend-icon="mdi-image"
                ></v-file-input>
                
                <!-- Show current logo if editing and no new file selected -->
                <v-img
                  v-if="editingGroup && editingGroup.mainLogo && !logoFile"
                  :src="getLogoUrl(editingGroup.mainLogo)"
                  max-height="100"
                  max-width="200"
                  class="mb-4"
                  contain
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
  
                <!-- Participant Logos -->
                <v-card class="my-4" outlined>
                  <v-card-title>
                    {{ t("Partners") }}
                    <v-spacer></v-spacer>
                    <v-btn small color="primary" @click="addParticipant">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <v-row
                      v-for="(participant, index) in participants"
                      :key="index"
                      align="center"
                      class="mb-2"
                    >
                      <v-col cols="5">
                        <v-text-field
                          v-model="participant.name"
                          :label="t('Organization Name')"
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="5">
                        <v-file-input
                          v-model="participant.logoFile"
                          label="Logo"
                          accept="image/*"
                          dense
                        ></v-file-input>
                      </v-col>
                      <v-col cols="2">
                        <v-btn icon small @click="removeParticipant(index)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="cancelEdit">{{ t("Cancel") }}</v-btn>
              <v-btn
                color="primary"
                :disabled="!formValid || selectedHaikuboxes.length === 0 || !!slugError || checkingSlug"
                @click="saveGroup"
              >
                {{ editingGroup ? t('Update') : t('Create') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-snackbar v-model="copiedToast" :timeout="2000" color="success">
          {{ t("Copied") }}
        </v-snackbar>
      </v-container>
    </div>
  </template>
  
  <script>
  
  import { API, Storage } from "aws-amplify";
  import { latLng } from "leaflet";
  import { LMap, LTileLayer, LMarker, LTooltip } from "vue2-leaflet";
  import {
    listHaikuboxGroups,
    groupByOwner,
    haikuboxByOwner,
    requestByOwner,
    pendingByGroupOwner,
    getDevice,
    createGroupJoinRequestWithNotification
  } from "../graphql/queries";
  import {
    createHaikuboxGroups,
    updateHaikuboxGroups,
    deleteHaikuboxGroups,
    updateGroupJoinRequest,
    updateGroupJoinPending,
    deleteGroupJoinPending
  } from "../graphql/mutations";
  import { getGroupKey, setNewGroupKey, removeGroupKey, shareLink } from "../groupAccess";

  export default {
    name: "Groups",
    props: ["user", "email"],
    components: {
      LMap,
      LTileLayer,
      LMarker,
      LTooltip
    },
    data() {
      return {
        // Groups data
        allGroups: [],
        myGroups: [],
        userDevices: [],
        joinRequests: [],
        membershipData: [],
        
        // UI state
        showCreateDialog: false,
        showJoinGroupDialog: false,
        editingGroup: null,
        selectedGroup: null,
        selectedMapGroup: null,
        formValid: false,
        joinLoading: false,
        
        // Slug validation state
        checkingSlug: false,
        slugAvailable: false,
        slugError: null,
        slugCheckTimeout: null,
        existingSlugs: new Set(), // Keep slugs in memory
        
        // Management tabs
        managementTab: 0,
        
        // Search and filter
        searchQuery: "",
        filterStatus: "all",
        sortBy: "name",
        currentPage: 1,
        groupsPerPage: 10,
        
        // Form data
        selectedHaikuboxes: [],
        devicesToAdd: [],
        availableDevicesForGroup: [],
        joinMessage: "",
        logoFile: null,
        participants: [],
        groupForm: {
          groupName: "",
          groupSlug: "",
          textDescription: "",
          linkToOrg: "",
          mapMarker: "",
          hiddenSpecies: [],
          minLevel: "",
          allowDatePicker: true,
          allowCsvDownload: true,
          allowAudioDownload: true
        },
        speciesList: [], // [spCode, English name, scientific name, ...] from species-list.json
        minLevelOptions: [
          { text: "All detections", value: "" },
          { text: "Medium and high confidence", value: "med" },
          { text: "High confidence only", value: "high" }
        ],
        accessKey: null, // current key, or null when the group is open
        accessKeyLoading: false,
        accessKeyError: "",
        copiedToast: false,

        // Options
        statusFilters: [
          { text: "All Groups", value: "all" },
          { text: "Not Joined", value: "not_joined" },
          { text: "Joined", value: "joined" },
          { text: "Owner", value: "owner" }
        ],
        sortOptions: [
          { text: "Name", value: "name" },
          { text: "Station Count", value: "stations" },
          { text: "Recently Created", value: "created" }
        ],
        membershipHeaders: [
          { text: "Group Name", value: "groupName" },
          { text: "Your Devices", value: "devices" },
          { text: "Status", value: "status" },
          { text: "Actions", value: "actions", sortable: false }
        ],
        
        // Validation rules
        slugRules: [
          v => !!v || "URL slug is required",
          v => !v || /^[a-z0-9-]+$/.test(v) || "Only lowercase letters, numbers, and hyphens allowed",
          v => !v || v.length >= 3 || "Must be at least 3 characters",
          v => !v || v.length <= 50 || "Must be less than 50 characters",
          v => this.validateSlugUniqueness(v)
        ],
        urlRules: [
          v => !v || v.startsWith('http://') || v.startsWith('https://') || "URL must start with http:// or https://"
        ],
        
        // i18n
        currentTranslations: {},
        selectedLanguage: "en",
        languages: ["da", "de", "en", "es", "fi", "fr", "it", "nl", "no", "pt", "sv"],

        // Image handling
        failedImages: new Set(),
        useSignedUrls: false, // Set to true if direct S3 URLs don't work
        
        // Map settings
        zoom: 3,
        center: latLng(39, -94),
        mapOptions: {
          zoomSnap: 0.5,
        },
        urlStates: "https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}",
        attribution: "Tiles &copy; Esri &mdash; National Geographic, Esri, DeLorme, NAVTEQ, UNEP-WCMC, USGS, NASA, ESA, METI, NRCAN, GEBCO, NOAA, iPC"
      };
    },
    computed: {
      // Hidden-species picker: saved by species code, shown in the owner's language.
      speciesOptions() {
        return this.speciesList
          .map(([spCode, cn, sn]) => ({ value: spCode, text: this.t(spCode, cn), sn }))
          .sort((a, b) => a.text.localeCompare(b.text, this.selectedLanguage));
      },
      selectedDevicesWithNames() {
        return this.selectedHaikuboxes.map(serial => {
          const device = this.userDevices.find(d => d.serial === serial);
          return {
            serial: serial,
            display: device ? `${device.name} (${serial})` : serial
          };
        });
      },
      filteredGroups() {
        let groups = [...this.allGroups];
        
        // Search filter
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          groups = groups.filter(g => 
            g.groupName.toLowerCase().includes(query) ||
            (g.textDescription && g.textDescription.toLowerCase().includes(query))
          );
        }
        
        // Status filter
        if (this.filterStatus !== "all") {
          groups = groups.filter(g => {
            if (this.filterStatus === "owner") return g.owner === this.user;
            if (this.filterStatus === "joined") return g.memberDeviceCount > 0;
            if (this.filterStatus === "not_joined") return !g.memberDeviceCount && g.owner !== this.user;
            return true;
          });
        }
        
        // Sort
        groups.sort((a, b) => {
          if (this.sortBy === "name") return a.groupName.localeCompare(b.groupName);
          if (this.sortBy === "stations") return b.haikuboxes.length - a.haikuboxes.length;
          if (this.sortBy === "created") return new Date(b.createdAt) - new Date(a.createdAt);
          return 0;
        });
        
        return groups;
      },
      paginatedGroups() {
        const start = (this.currentPage - 1) * this.groupsPerPage;
        const end = start + this.groupsPerPage;
        return this.filteredGroups.slice(start, end);
      },
      totalPages() {
        return Math.ceil(this.filteredGroups.length / this.groupsPerPage);
      }
    },
    async mounted() {
      // Load language
      const savedLang = localStorage.getItem("haikuboxLanguage");
      if (savedLang) {
        this.setLanguage(savedLang);
      } else {
        const browserLang = (navigator.language || navigator.languages[0]).split("-")[0];
        this.setLanguage(browserLang);
      }

      // Only load data if user is available
      if (this.user) {
        await this.loadAllData();
      }

      this.loadSpeciesList();
    },
    watch: {
      // Watch for user prop changes
      user: {
        immediate: false,
        handler(newUser, oldUser) {
          if (newUser && newUser !== oldUser) {
            this.loadAllData();
          }
        }
      },
      // Watch logoFile changes
      logoFile(newFile) {
        if (newFile) {
          this.onLogoSelected(newFile);
        }
      }
    },
    methods: {
      setLanguage(lang) {
        if (!this.languages.includes(lang)) lang = "en";
        this.loadTranslations(lang).then((translations) => {
          this.currentTranslations = translations;
        });
        localStorage.setItem("haikuboxLanguage", lang);
        this.selectedLanguage = lang;
      },
      loadTranslations(lang) {
        return fetch(`/I18N/${lang}.json`)
          .then((response) => response.json())
          .then((translations) => translations);
      },
      t(key, fallback) {
        return this.currentTranslations[key] || fallback || key;
      },
      async loadAllData() {
        // Load groups and devices first
        await Promise.all([
          this.loadGroups(),
          this.loadUserDevices()
        ]);
        
        // Then load join requests (which needs groups to be loaded)
        await this.loadJoinRequests();
        
        // Finally load membership data
        await this.loadMembershipData();
      },
     
      async loadGroups() {
    try {
        // Clear failed images tracking before reloading
        this.failedImages.clear();
        
        // Load all groups for map
        const allGroupsData = await API.graphql({
        query: listHaikuboxGroups
        });
        
        // Clear and rebuild slug set
        this.existingSlugs.clear();
        allGroupsData.data.listHaikuboxGroups.items.forEach(group => {
        if (group.groupSlug) {
            this.existingSlugs.add(group.groupSlug);
        }
        });
        
        // Load user's groups only if user is defined
        if (this.user) {
        const myGroupsData = await API.graphql({
            query: groupByOwner,
            variables: { owner: this.user }
        });
        
        this.myGroups = myGroupsData.data.groupByOwner.items || [];
        } else {
        this.myGroups = [];
        }
        
        // Process all groups to add membership info and location
        this.allGroups = await Promise.all(
        allGroupsData.data.listHaikuboxGroups.items.map(async (group) => {
        // Count user's devices in this group
        const memberDevices = this.getUserDevicesInGroup(group);
        const pendingDevices = this.user ? await this.getPendingDevicesForGroup(group.id) : [];
        
        let location = null;
        //console.log(group);
        // Use coordinates from the group's arrays
        if (group.haikuboxLats && group.haikuboxLons && 
            group.haikuboxLats.length > 0 && group.haikuboxLons.length > 0) {
        
        // If mapMarker is set, use that specific device's coordinates
        if (group.mapMarker && group.haikuboxes) {
            const markerIndex = group.haikuboxes.indexOf(group.mapMarker);
            if (markerIndex !== -1 && 
                group.haikuboxLats[markerIndex] !== null &&
                group.haikuboxLons[markerIndex] !== null) {
            location = [
                group.haikuboxLats[markerIndex],
                group.haikuboxLons[markerIndex]
            ];
            }
        }
        
        // If no mapMarker or not found, use average of all coordinates
        if (!location) {
            const validLats = group.haikuboxLats.filter(lat => lat !== null && lat !== undefined);
            const validLons = group.haikuboxLons.filter(lon => lon !== null && lon !== undefined);
            
            if (validLats.length > 0 && validLons.length > 0) {
            const avgLat = validLats.reduce((sum, lat) => sum + lat, 0) / validLats.length;
            const avgLon = validLons.reduce((sum, lon) => sum + lon, 0) / validLons.length;
            
            location = [avgLat, avgLon];
            }
        }
        }

        // Default location if not found (center of US)
        if (!location) {
        location = [39, -94];
        }
                
                return {
                ...group,
                memberDeviceCount: memberDevices.length,
                memberDevices,
                pendingDevices,
                location
                };
            })
            );
        } catch (error) {
            console.error("Error loading groups:", error);
            this.allGroups = [];
            this.myGroups = [];
        }
    },

      async loadUserDevices() {
        // Check if user is logged in
        if (!this.user) {
          console.log("No user found, skipping device load");
          this.userDevices = [];
          return;
        }
        
        try {
          const devicesData = await API.graphql({
            query: haikuboxByOwner,
            variables: { owner: this.user }
          });
          
          this.userDevices = devicesData.data.haikuboxByOwner.items || [];
        } catch (error) {
          console.error("Error loading user devices:", error);
          this.userDevices = [];
        }
      },
      async loadJoinRequests() {
        // Load pending join requests for groups the user owns
        try {
          const allJoinRequests = [];
          
          // Use the already loaded myGroups
          if (this.myGroups && this.myGroups.length > 0) {
            // For each group owned by the user, get pending requests
            for (const group of this.myGroups) {
              const pendingRequests = await API.graphql({
                query: pendingByGroupOwner,
                variables: { 
                  groupId: group.id,
                  filter: { status: { eq: "pending" } }
                }
              });
              
              if (pendingRequests.data.pendingByGroupOwner.items.length > 0) {
                // Add group name to each request for display purposes
                const requestsWithGroupName = pendingRequests.data.pendingByGroupOwner.items.map(req => ({
                  ...req,
                  groupName: group.groupName
                }));
                allJoinRequests.push(...requestsWithGroupName);
              }
            }
          }
          
          this.joinRequests = allJoinRequests;
        } catch (error) {
          console.error("Error loading join requests:", error);
          this.joinRequests = [];
        }
      },
      async loadMembershipData() {
        // Build membership table data
        const memberships = [];
        
        for (const group of this.allGroups) {
          if (group.memberDeviceCount > 0) {
            const devices = this.userDevices.filter(d => 
              group.haikuboxes.includes(d.serial)
            );
            
            memberships.push({
              group,
              groupName: group.groupName,
              devices,
              status: "Active",
              canAddMore: this.userDevices.length > devices.length
            });
          }
        }
        
        this.membershipData = memberships;
      },
      getUserDevicesInGroup(group) {
        return this.userDevices.filter(device => 
          group.haikuboxes.includes(device.serial)
        );
      },
      async getPendingDevicesForGroup(groupId) {
        // Check if user exists
        if (!this.user) {
          return [];
        }
        
        try {
          // Query pending requests for this user and group
          const pendingRequests = await API.graphql({
            query: requestByOwner,
            variables: {
              groupId: groupId,
              filter: { 
                owner: { eq: this.user },
                status: { eq: "pending" }
              }
            }
          });
          
          return pendingRequests.data.requestByOwner.items.map(req => req.haikuboxSerial);
        } catch (error) {
          console.error("Error getting pending devices:", error);
          return [];
        }
      },
      getStatusColor(group) {
        if (this.user && group.owner === this.user) return "blue";
        if (group.memberDeviceCount > 0) return "green";
        if (group.pendingDevices && group.pendingDevices.length > 0) return "orange";
        return "grey";
      },
      getStatusText(group) {
        if (this.user && group.owner === this.user) return "Owner";
        if (group.memberDeviceCount > 0) return `${group.memberDeviceCount} devices joined`;
        if (group.pendingDevices && group.pendingDevices.length > 0) return "Pending";
        return "Not joined";
      },
      canRequestToJoin(group) {
        // Can request if:
        // 1. User is logged in
        // 2. Not the owner
        // 3. Have devices not in the group
        if (!this.user) return false;
        if (group.owner === this.user) return false;
        const availableDevices = this.userDevices.filter(d => 
          !group.haikuboxes.includes(d.serial)
        );
        return availableDevices.length > 0;
      },
      getGroupLocation(group) {
        return group.location || [39, -94];
      },
      selectGroupOnMap(group) {
        this.selectedMapGroup = group;
        // Scroll to group in list
        const element = document.getElementById(`group-${group.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      },
      getDeviceName(serial) {
        const device = this.userDevices.find(d => d.serial === serial);
        return device ? device.name : serial;
      },
      getLogoUrl(s3Path) {
        if (!s3Path) return "";
        if (s3Path.startsWith("http")) return s3Path;
        
        // // Get the bucket name and region from Amplify config if available
        const bucketName = "haikubox-media152750-prod"; // Update this if your bucket name is different
        const region = "us-east-1"
        const url = `https://${bucketName}.s3.${region}.amazonaws.com/public/${s3Path}`;
        
        return url;
      },
      handleImageError(groupId) {
        console.error("Failed to load image for group:", groupId);
        this.failedImages.add(groupId);
        // Force re-render
        this.$forceUpdate();
      },
      onImageError(error) {
        console.error("Image load error:", error);
      },
      getRequestCount(groupId) {
        return this.joinRequests.filter(r => r.groupId === groupId).length;
      },
      getGroupName(groupId) {
        const group = this.myGroups.find(g => g.id === groupId);
        return group ? group.groupName : groupId;
      },
      viewGroup(group) {
        window.location.href = `/#/groups/${group.groupSlug}`;
      },
      showJoinDialog(group) {
        if (!this.user) {
          alert("Please log in to join a group.");
          return;
        }
        
        this.selectedGroup = group;
        this.devicesToAdd = [];
        
        // Build available devices list
        this.availableDevicesForGroup = this.userDevices.map(device => {
          const isMember = group.haikuboxes.includes(device.serial);
          const isPending = group.pendingDevices && 
            group.pendingDevices.includes(device.serial);
          
          return {
            ...device,
            status: isMember ? 'member' : (isPending ? 'pending' : 'available'),
            disabled: isMember || isPending
          };
        });
        
        this.showJoinGroupDialog = true;
      },
      async submitJoinRequest() {
        if (this.devicesToAdd.length === 0) return;
        if (!this.user) {
          alert("Please log in to join a group.");
          return;
        }
        
        this.joinLoading = true;
        let successCount = 0;
        let errorMessages = [];
        
        try {
          // Create join requests for each selected device using Lambda
          for (const serial of this.devicesToAdd) {
            const device = this.userDevices.find(d => d.serial === serial);
            
            try {
              const result = await API.graphql({
                query: createGroupJoinRequestWithNotification,
                variables: {
                  groupId: this.selectedGroup.id,
                  haikuboxSerial: serial,
                  haikuboxName: device.name
                }
              });
              
              const response = JSON.parse(result.data.createGroupJoinRequestWithNotification);
              
              if (response.success) {
                successCount++;
              } else {
                errorMessages.push(`${device.name}: ${response.error}`);
              }
            } catch (error) {
              console.error(`Error for device ${device.name}:`, error);
              errorMessages.push(`${device.name}: Failed to send request`);
            }
          }
          
          // Show results
          if (successCount > 0) {
            alert(`Successfully sent join request for ${successCount} device(s)`);
          }
          
          if (errorMessages.length > 0) {
            alert(`Some requests failed:\n${errorMessages.join('\n')}`);
          }
          
          if (successCount > 0) {
            this.showJoinGroupDialog = false;
            await this.loadAllData();
          }
          
        } catch (error) {
          console.error("Error submitting join requests:", error);
          alert("Error sending join requests. Please try again.");
        } finally {
          this.joinLoading = false;
        }
      },
      cancelJoin() {
        this.showJoinGroupDialog = false;
        this.selectedGroup = null;
        this.devicesToAdd = [];
      },
      
      async leaveGroup(membership) {
  if (!confirm(`Are you sure you want to remove your devices from "${membership.groupName}"?`)) {
    return;
  }
  
  try {
    // Get current group data to maintain coordinate arrays
    const group = membership.group;
    const devicesToRemove = membership.devices.map(d => d.serial);
    
    // Build new arrays excluding removed devices
    const updatedHaikuboxes = [];
    const updatedLats = [];
    const updatedLons = [];
    
    for (let i = 0; i < group.haikuboxes.length; i++) {
      const serial = group.haikuboxes[i];
      if (!devicesToRemove.includes(serial)) {
        updatedHaikuboxes.push(serial);
        if (group.haikuboxLats && group.haikuboxLats[i] !== undefined) {
          updatedLats.push(group.haikuboxLats[i]);
        }
        if (group.haikuboxLons && group.haikuboxLons[i] !== undefined) {
          updatedLons.push(group.haikuboxLons[i]);
        }
      }
    }
    
    // Check if mapMarker is being removed and update if necessary
    let updatedMapMarker = group.mapMarker;
    if (devicesToRemove.includes(group.mapMarker)) {
      // If the mapMarker is being removed, set it to the first remaining device
      updatedMapMarker = updatedHaikuboxes.length > 0 ? updatedHaikuboxes[0] : null;
    }
    
    await API.graphql({
      query: updateHaikuboxGroups,
      variables: {
        input: {
          id: membership.group.id,
          haikuboxes: updatedHaikuboxes,
          haikuboxLats: updatedLats,
          haikuboxLons: updatedLons,
          mapMarker: updatedMapMarker
        }
      }
    });
    
    await this.loadAllData();
  } catch (error) {
    console.error("Error leaving group:", error);
    alert("Error leaving group. Please try again.");
  }
},


      createNewGroup() {
        this.editingGroup = null;
        this.resetForm();
        this.showCreateDialog = true;
      },
      editGroup(group) {
        this.editingGroup = group;
        this.groupForm = {
          groupName: group.groupName || "",
          groupSlug: group.groupSlug || "",
          textDescription: group.textDescription || "",
          linkToOrg: group.linkToOrg || "",
          mapMarker: group.mapMarker || "",
          // Saved values until normalizeHiddenSpecies converts any names.
          hiddenSpecies: group.hiddenSpecies || [],
          minLevel: group.minLevel || "",
          // Unset (groups saved before these settings) means on.
          allowDatePicker: group.allowDatePicker !== false,
          allowCsvDownload: group.allowCsvDownload !== false,
          allowAudioDownload: group.allowAudioDownload !== false
        };
        this.selectedHaikuboxes = group.haikuboxes || [];
        
        // Reset slug validation state
        this.slugAvailable = true;
        this.slugError = null;
        
        // Load participants
        this.participants = [];
        if (group.logoList && group.logoName) {
          for (let i = 0; i < group.logoList.length; i++) {
            this.participants.push({
              name: group.logoName[i] || '',
              logo: group.logoList[i] || '',
              logoFile: null
            });
          }
        }
        
        // Clear any previous file selection
        this.logoFile = null;

        this.loadAccessKey(group);
        this.normalizeHiddenSpecies(group.hiddenSpecies).then((codes) => {
          // Only if the dialog is still on this group.
          if (this.editingGroup === group) this.groupForm.hiddenSpecies = codes;
        });
        this.showCreateDialog = true;
      },
      async deleteGroup(group) {
        if (!confirm(`Are you sure you want to delete "${group.groupName}"?`)) {
          return;
        }
        
        try {
          await API.graphql({
            query: deleteHaikuboxGroups,
            variables: { input: { id: group.id } }
          });
          // Don't leave the group's key row behind.
          await removeGroupKey(group.id).catch(() => {});

          // Remove slug from our set
          if (group.groupSlug) {
            this.existingSlugs.delete(group.groupSlug);
          }
          
          await this.loadAllData();
        } catch (error) {
          console.error("Error deleting group:", error);
          alert("Error deleting group. Please try again.");
        }
      },
      // The species the model detects. Hidden species are saved by code, so
      // renames and translations can't break them.
      loadSpeciesList() {
        if (!this.speciesListLoaded) {
          this.speciesListLoaded = fetch("/species-list.json")
            .then((r) => r.json())
            .then((list) => {
              this.speciesList = list;
            })
            .catch((error) => {
              console.error("Error loading species list:", error);
            });
        }
        return this.speciesListLoaded;
      },
      // Groups saved before hidden species used codes hold English names.
      // Convert those to codes, so the next save stores codes.
      async normalizeHiddenSpecies(hiddenSpecies) {
        await this.loadSpeciesList();
        const codes = new Set(this.speciesList.map(([spCode]) => spCode));
        const codeByName = {};
        for (const [spCode, cn] of this.speciesList) {
          codeByName[cn.trim().toLowerCase()] = spCode;
        }
        const normalized = (hiddenSpecies || []).map((value) => {
          if (codes.has(value)) return value;
          return codeByName[String(value).trim().toLowerCase()] || value;
        });
        return [...new Set(normalized)];
      },
      filterSpecies(item, queryText) {
        const query = queryText.toLowerCase();
        return (
          item.text.toLowerCase().includes(query) ||
          (item.sn || "").toLowerCase().includes(query) ||
          item.value.includes(query)
        );
      },
      async loadAccessKey(group) {
        this.accessKey = null;
        this.accessKeyError = "";
        this.accessKeyLoading = true;
        try {
          this.accessKey = await getGroupKey(group.id);
        } catch (error) {
          console.error("Error loading access key:", error);
          this.accessKeyError = "Couldn't load the access key.";
        } finally {
          this.accessKeyLoading = false;
        }
      },
      async newAccessKey() {
        if (this.accessKey && !confirm("Generate a new key? Links and keys already shared will stop working.")) {
          return;
        }
        this.accessKeyLoading = true;
        this.accessKeyError = "";
        try {
          this.accessKey = await setNewGroupKey(this.editingGroup.id, this.user);
        } catch (error) {
          console.error("Error setting access key:", error);
          this.accessKeyError = "Couldn't save the access key.";
        } finally {
          this.accessKeyLoading = false;
        }
      },
      async removeAccessKey() {
        if (!confirm("Remove the key? Anyone with the group's link will be able to see its detections.")) {
          return;
        }
        this.accessKeyLoading = true;
        this.accessKeyError = "";
        try {
          await removeGroupKey(this.editingGroup.id);
          this.accessKey = null;
        } catch (error) {
          console.error("Error removing access key:", error);
          this.accessKeyError = "Couldn't remove the access key.";
        } finally {
          this.accessKeyLoading = false;
        }
      },
      accessKeyLink() {
        return shareLink(this.editingGroup.groupSlug, this.accessKey);
      },
      copyText(text) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            this.copiedToast = true;
          })
          .catch(() => {});
      },
      addParticipant() {
        this.participants.push({
          name: "",
          logo: "",
          logoFile: null
        });
      },
      removeParticipant(index) {
        this.participants.splice(index, 1);
      },
      onLogoSelected(file) {
        // File is automatically bound to logoFile via v-model
        if (!file) {
          console.log("No file selected");
          return;
        }
        
        // Validate file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert("Logo file size must be less than 5MB");
          this.logoFile = null;
          return;
        }
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert("Please select an image file");
          this.logoFile = null;
          return;
        }
        
        console.log("Logo file selected:", file.name);
      },
      onSlugInput(value) {
        // Clear any previous timeout
        if (this.slugCheckTimeout) {
          clearTimeout(this.slugCheckTimeout);
        }
        
        // Reset state
        this.slugError = null;
        this.slugAvailable = false;
        
        if (!value) {
          this.checkingSlug = false;
          return;
        }
        
        // Basic validation first
        if (!/^[a-z0-9-]+$/.test(value)) {
          this.checkingSlug = false;
          return;
        }
        
        if (value.length < 3) {
          this.checkingSlug = false;
          return;
        }
        
        // Check after user stops typing for 500ms
        this.checkingSlug = true;
        this.slugCheckTimeout = setTimeout(() => {
          this.checkSlugAvailability(value);
        }, 500);
      },
      checkSlugAvailability(slug) {
        // If editing, check if it's the same as the current slug
        if (this.editingGroup && this.editingGroup.groupSlug === slug) {
          this.slugAvailable = true;
          this.checkingSlug = false;
          return;
        }
        
        // Check against our in-memory set
        if (this.existingSlugs.has(slug)) {
          this.slugError = "This URL slug is already taken";
          this.slugAvailable = false;
        } else {
          this.slugAvailable = true;
          this.slugError = null;
        }
        
        this.checkingSlug = false;
      },
      validateSlugUniqueness(value) {
        if (!value) return true; // Let other rules handle empty value
        
        // If editing and it's the same slug, it's valid
        if (this.editingGroup && this.editingGroup.groupSlug === value) {
          return true;
        }
        
        // Check against our in-memory set
        if (this.existingSlugs.has(value)) {
          return "This URL slug is already taken";
        }
        
        return true;
      },
      cancelEdit() {
        this.showCreateDialog = false;
        this.editingGroup = null;
        // Clear the form but don't reset validation until after dialog closes
        this.logoFile = null;
        
        // Reset slug validation state
        this.slugAvailable = false;
        this.slugError = null;
        this.checkingSlug = false;
        if (this.slugCheckTimeout) {
          clearTimeout(this.slugCheckTimeout);
        }
      },
      resetForm() {
        this.groupForm = {
          groupName: "",
          groupSlug: "",
          textDescription: "",
          linkToOrg: "",
          mapMarker: "",
          hiddenSpecies: [],
          minLevel: "",
          allowDatePicker: true,
          allowCsvDownload: true,
          allowAudioDownload: true
        };
        this.accessKey = null;
        this.accessKeyError = "";
        this.selectedHaikuboxes = [];
        this.logoFile = null;
        this.participants = [];
        this.failedImages.clear(); // Clear failed images tracking
        
        // Reset slug validation state
        this.slugAvailable = false;
        this.slugError = null;
        this.checkingSlug = false;
        
        // Reset form validation after next tick to avoid errors
        this.$nextTick(() => {
          if (this.$refs.groupForm) {
            this.$refs.groupForm.resetValidation();
          }
        });
      },
      async saveGroup() {
        // Check form validation
        const isValid = this.$refs.groupForm && this.$refs.groupForm.validate();
        if (!isValid) return;
        
        if (!this.user) {
          alert("You must be logged in to create a group.");
          return;
        }
        
        // Double-check slug availability
        if (this.slugError || (!this.editingGroup && this.existingSlugs.has(this.groupForm.groupSlug))) {
          alert("Please choose a unique URL slug.");
          return;
        }
        
        try {
          // Upload main logo if provided
          let mainLogoPath = this.editingGroup?.mainLogo || "";
          if (this.logoFile) {
            const timestamp = Date.now();
            const fileName = this.logoFile.name.replace(/[^a-zA-Z0-9.-]/g, '_'); // Sanitize filename
            const logoKey = `groups/${timestamp}_${fileName}`;
            
            console.log("Uploading logo to:", logoKey);
            
            const uploadResult = await Storage.put(logoKey, this.logoFile, {
              contentType: this.logoFile.type,
              level: "public",
              progressCallback(progress) {
                console.log(`Uploaded: ${progress.loaded}/${progress.total}`);
              }
            });
            
            console.log("Upload result:", uploadResult);
            mainLogoPath = uploadResult.key || logoKey;
            
            // Verify the image is accessible
            await this.verifyImageUrl(mainLogoPath);
          }
          
          // Upload participant logos
          const logoList = [];
          const logoName = [];
          
          for (const participant of this.participants) {
            if (participant.name) {
              logoName.push(participant.name);
              
              if (participant.logoFile) {
                const timestamp = Date.now();
                const fileName = participant.logoFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                const partLogoKey = `groups/${timestamp}_${fileName}`;
                
                const uploadResult = await Storage.put(partLogoKey, participant.logoFile, {
                  contentType: participant.logoFile.type,
                  level: "public"
                });
                
                logoList.push(uploadResult.key || partLogoKey);
              } else {
                logoList.push(participant.logo || "");
              }
            }
          }
          
          // Collect coordinates for selected haikuboxes
          const haikuboxLats = [];
          const haikuboxLons = [];
          
          for (const serial of this.selectedHaikuboxes) {
            const device = this.userDevices.find(d => d.serial === serial);
            if (device) {
              // Round to 3 decimal places
              haikuboxLats.push(Math.round(device.lat * 1000) / 1000);
              haikuboxLons.push(Math.round(device.lon * 1000) / 1000);
            } else {
              // If device not found in userDevices, push null or default coordinates
              console.warn(`Device ${serial} not found in userDevices`);
              haikuboxLats.push(null);
              haikuboxLons.push(null);
            }
          }
          
          const groupData = {
            ...this.groupForm,
            minLevel: this.groupForm.minLevel || null,
            owner: this.user,
            haikuboxes: this.selectedHaikuboxes,
            haikuboxLats,
            haikuboxLons,
            mainLogo: mainLogoPath,
            logoList,
            logoName
          };
          
          if (this.editingGroup) {
            // Update existing group
            await API.graphql({
              query: updateHaikuboxGroups,
              variables: { 
                input: {
                  id: this.editingGroup.id,
                  ...groupData
                }
              }
            });
            
            // Update our slug set if slug changed
            if (this.editingGroup.groupSlug !== groupData.groupSlug) {
              this.existingSlugs.delete(this.editingGroup.groupSlug);
              this.existingSlugs.add(groupData.groupSlug);
            }
          } else {
            // Create new group
            groupData.id = `${this.user}_${Date.now()}`;
            await API.graphql({
              query: createHaikuboxGroups,
              variables: { input: groupData }
            });
            
            // Add new slug to our set
            this.existingSlugs.add(groupData.groupSlug);
          }
          
          this.showCreateDialog = false;
          this.editingGroup = null;
          this.resetForm();
          await this.loadAllData();
          
        } catch (error) {
          console.error("Error saving group:", error);
          alert("Error saving group. Please check that the URL slug is unique.");
        }
      },
      async approveRequest(request) {
        try {
          // Update both tables - pending notification and original request
          const updatePromises = [
            // Update the pending notification
            API.graphql({
              query: updateGroupJoinPending,
              variables: {
                input: {
                  id: request.id,
                  status: "approved"
                }
              }
            }),
            // Update the original request (using IAM auth)
            API.graphql({
              query: updateGroupJoinRequest,
              variables: {
                input: {
                  id: request.id,
                  status: "approved"
                }
              },
              authMode: 'AWS_IAM'
            })
          ];
          
          await Promise.all(updatePromises);
          
          // Add haikubox to group with coordinates
          const group = this.myGroups.find(g => g.id === request.groupId);
          if (group) {
            // Get device coordinates
            const deviceId = request.haikuboxSerial.split("_")[1];
            let lat = null;
            let lon = null;
            
            try {
              const deviceData = await API.graphql({
                query: getDevice,
                variables: { id: deviceId }
              });
              
              if (deviceData.data.getDevice) {
                lat = Math.round(deviceData.data.getDevice.lat * 1000) / 1000;
                lon = Math.round(deviceData.data.getDevice.lon * 1000) / 1000;
              }
            } catch (error) {
              console.error("Error getting device coordinates:", error);
            }
            
            // Update arrays
            const updatedHaikuboxes = [...(group.haikuboxes || []), request.haikuboxSerial];
            const updatedLats = [...(group.haikuboxLats || []), lat];
            const updatedLons = [...(group.haikuboxLons || []), lon];
            
            await API.graphql({
              query: updateHaikuboxGroups,
              variables: {
                input: {
                  id: group.id,
                  haikuboxes: updatedHaikuboxes,
                  haikuboxLats: updatedLats,
                  haikuboxLons: updatedLons
                }
              }
            });
          }
          
          await this.loadAllData();
          alert(`Approved ${request.haikuboxName} to join ${request.groupName}`);
        } catch (error) {
          console.error("Error approving request:", error);
          alert("Error approving request. Please try again.");
        }
      },
      async rejectRequest(request) {
        try {
          // Update both tables
          const updatePromises = [
            // Update the pending notification
            API.graphql({
              query: updateGroupJoinPending,
              variables: {
                input: {
                  id: request.id,
                  status: "rejected"
                }
              }
            }),
            // Update the original request (using IAM auth)
            API.graphql({
              query: updateGroupJoinRequest,
              variables: {
                input: {
                  id: request.id,
                  status: "rejected"
                }
              },
              authMode: 'AWS_IAM'
            })
          ];
          
          await Promise.all(updatePromises);
          
          // Optionally delete the pending notification
          await API.graphql({
            query: deleteGroupJoinPending,
            variables: {
              input: { id: request.id }
            }
          });
          
          await this.loadJoinRequests();
          alert(`Rejected request from ${request.haikuboxName}`);
        } catch (error) {
          console.error("Error rejecting request:", error);
          alert("Error rejecting request. Please try again.");
        }
      },
      onDialogChange(value) {
        if (!value) {
          // Dialog is closing, reset form after a delay
          setTimeout(() => {
            this.resetForm();
          }, 300);
        }
      },
      // Test method to verify S3 URL accessibility
      async verifyImageUrl(s3Path) {
        if (!s3Path) return;
        
        console.log("Testing image accessibility for:", s3Path);
        
        try {
          // Method 1: Direct URL
          const directUrl = this.getLogoUrl(s3Path);
          console.log("Direct URL:", directUrl);
          
          // Method 2: Signed URL via Storage.get
          const cleanPath = s3Path.replace(/^public\//, '');
          const signedUrl = await Storage.get(cleanPath, { 
            level: 'public',
            expires: 3600
          });
          console.log("Signed URL:", signedUrl);
          
          // Test direct URL with fetch
          try {
            const response = await fetch(directUrl, { mode: 'cors' });
            console.log("Direct URL response status:", response.status);
          } catch (error) {
            console.error("Direct URL fetch error:", error);
          }
          
          return { directUrl, signedUrl };
        } catch (error) {
          console.error("Error verifying image URL:", error);
        }
      },
      formatDate(dateString) {
        return new Date(dateString).toLocaleDateString();
      }
    }
  };
  </script>
  
  <style scoped>
  #groups {
    font-family: Quicksand, Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  /* Fix z-index issue with map appearing over dialogs */
  .groups-map-container {
    position: relative;
    z-index: 1;
  }
  
  .groups-map-container >>> .leaflet-container {
    z-index: 1 !important;
  }
  
  .groups-map-container >>> .leaflet-pane {
    z-index: 1 !important;
  }
  
  .groups-map-container >>> .leaflet-top,
  .groups-map-container >>> .leaflet-bottom {
    z-index: 2 !important;
  }
  </style>
  
  <style>
  /* Ensure Vuetify dialogs stay on top */
  .v-dialog__container {
    z-index: 2000 !important;
  }
  
  .v-overlay {
    z-index: 1999 !important;
  }
  </style>